'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import HotspotItem from './HotspotItem';
import './styles.css';
import type { HotspotContainerProps } from './types';

/**
 * Renders a container for hotspot items and manages their open/close state.
 * Handles mouse interactions and outside click detection.
 *
 * When using inline styles a new object is created on every render, which cause re-rendering and is performance
 * issue, that's why we use useMemo
 * @param props - The component props.
 * @param props.children - The image or background content over which hotspots are positioned.
 * @param props.hotspots - List of hotspot data including position and content.
 * @param props.pictureIsLoaded - Flag indicating whether the picture is fully loaded.
 * @returns The rendered hotspot container.
 */
export function HotspotContainer(props: HotspotContainerProps) {
  const { children, hotspots, pictureIsLoaded } = props;
  const hotspotItems = hotspots?.items || [];
  const hasHotspots = Array.isArray(hotspotItems) && hotspotItems.length > 0;

  const [indexOpen, setIndexOpen] = useState<number | null>(null);
  const [delay, setDelay] = useState<NodeJS.Timeout | null>(null);
  const hotSpotContainerRef = useRef<HTMLDivElement>(null);
  const portalTargetRef = useRef<HTMLElement | null>(null);

  const handleOnMouseLeave = () => {
    if (delay) clearTimeout(delay);

    //If indexOpen is already -1, React still schedules a re-render.
    if (indexOpen !== -1) {
      setIndexOpen(-1);
    }
  };

  useEffect(() => {
    portalTargetRef.current = document.getElementById('hotspot-content-container');
  }, []);

  /**
   * Adds a global click listener to detect and handle clicks outside the hotspot container.
   *
   * If a click occurs outside the container referenced by `hotSpotContainerRef`,
   * it resets the open hotspot index to -1, effectively closing any open tooltip.
   *
   * This effect runs only once on mount and cleans up the event listener on unmount.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hotSpotContainerRef.current && !hotSpotContainerRef.current.contains(event.target as Node)) {
        setIndexOpen(-1);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  /**
   * Memoized list of rendered HotspotItem components.
   *
   * This avoids recalculating the list on every render unless its dependencies change.
   * It ensures performance by preventing unnecessary re-renders and avoids violations
   * of the React rules of hooks by keeping hooks at the top level.
   *
   * @returns {ReactElement[] | null} Array of HotspotItem components or null if no hotspots or image is not loaded.
   */
  const HotspotItems = useMemo(() => {
    if (!hasHotspots || !pictureIsLoaded) return null;

    return hotspotItems.map((item, index) => {
      const computedStyle = {
        top: item.top,
        left: `${parseFloat(item.left) + 1}%`,
      };

      return (
        <HotspotItem
          key={`hotspot-item-${index}`}
          delay={delay}
          setDelay={setDelay}
          index={index}
          indexOpen={indexOpen}
          setIndexOpen={setIndexOpen}
          isOpenedBydefault={item.isOpen}
          hotspotContent={item}
          style={computedStyle}
          portalTarget={portalTargetRef.current}
          constants={hotspots?.constants}
        />
      );
    });
  }, [hotspots, pictureIsLoaded, delay, indexOpen, setDelay, setIndexOpen]);

  return (
    <div aria-label="hotspot-container" className="hotspot-container" onMouseLeave={handleOnMouseLeave} ref={hotSpotContainerRef}>
      {children}

      {hasHotspots && pictureIsLoaded && <ul className="hotspot-item--container">{HotspotItems}</ul>}
    </div>
  );
}

export default HotspotContainer;
